<template>
  <!-- The same two tones as a details card, so the building's defaults read as one block -->
  <Card size="large" :variant="tone === 'secondary' ? 'outline-secondary' : 'primary'">
    <View direction="column" gap="md" class="listing-settings">
      <!-- Tour options -->
      <View direction="column" gap="sm">
        <!-- The note hugs its label, as a subtitle does; the badges keep the row's gap -->
        <View direction="column" gap="xs">
          <View justify="space-between" align="center" gap="md">
            <Text variant="label-medium">Tour options</Text>
            <Button variant="ghost" :size="editButtonSize" data-manage-action @click="emit('editTours')">
              <span>Edit</span>
            </Button>
          </View>
          <!-- As a details card's subtitle: the note reads small and quiet under the label -->
          <Text v-if="$slots['tours-note']" variant="body-small" color="secondary">
            <slot name="tours-note" />
          </Text>
        </View>
        <View gap="xs" align="center" wrap="wrap">
          <Badge
            v-if="tourTypes?.allowsInPersonTours"
            icon="user-01"
            label="In-person tour"
            variant="grey"
            :size="badgeSize"
          />
          <Badge
            v-if="tourTypes?.allowsVirtualTours"
            icon="video-recorder"
            label="Virtual tour"
            variant="grey"
            :size="badgeSize"
          />
          <Badge
            v-if="tourTypes?.allowsSelfGuidedTours"
            icon="key"
            label="Self-guided"
            variant="grey"
            :size="badgeSize"
          />
          <!-- Always shown once any tour type is set, on or off: it is a paid product, so an owner
               who has not turned it on still has to see that it exists. A building has none to show:
               ID checks are each unit's own. -->
          <Badge
            v-if="hasTourType && identityVerification !== undefined"
            icon="user-check-01"
            :label="identityVerification ? 'ID verification on' : 'ID verification off'"
            variant="grey"
            :size="badgeSize"
          />
          <Text v-if="!hasTourType" variant="body-medium" color="tertiary">Not set</Text>
        </View>
      </View>

      <Divider />

      <!-- Screening sensitivity -->
      <View direction="column" gap="sm">
        <!-- The note hugs its label, as a subtitle does; the badges keep the row's gap -->
        <View direction="column" gap="xs">
          <View justify="space-between" align="center" gap="md">
            <Text variant="label-medium">Screening sensitivity</Text>
            <Button variant="ghost" :size="editButtonSize" data-manage-action @click="emit('editScreening')">
              <span>Edit</span>
            </Button>
          </View>
          <!-- As a details card's subtitle: the note reads small and quiet under the label -->
          <Text v-if="$slots['screening-note']" variant="body-small" color="secondary">
            <slot name="screening-note" />
          </Text>
        </View>
        <Badge :label="screeningSensitivity || 'Not set'" icon="leads" variant="grey" :size="badgeSize" />
      </View>
    </View>

    <!-- The sheets the Edit buttons open -->
    <slot />
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Badge, Button, Card, Divider, Text, View, screenMobile } from '@tallo/design-system';

/**
 * How a listing is worked: the tours it offers and how strictly renters are screened. The property
 * dashboard shows a unit's own; the building page shows what its new units start with.
 */
// A default of undefined keeps Vue from reading an absent boolean prop as false
const props = withDefaults(
  defineProps<{
    tourTypes:
      | {
          allowsInPersonTours?: boolean | null;
          allowsVirtualTours?: boolean | null;
          allowsSelfGuidedTours?: boolean | null;
        }
      | null
      | undefined;
    /** Left out where ID checks do not apply, such as a building's defaults */
    identityVerification?: boolean | null;
    screeningSensitivity: string | null | undefined;
    /** Secondary sets apart what is only a default for new units, as on a details card */
    tone?: 'primary' | 'secondary';
  }>(),
  { identityVerification: undefined, tone: 'primary' },
);
const emit = defineEmits<{ editTours: []; editScreening: [] }>();

// On a phone the chips share one row only at the small size, and "Edit" needs a thumb-sized target
const badgeSize = computed(() => (screenMobile.value ? 'small' : 'medium'));
const editButtonSize = computed(() => (screenMobile.value ? 'medium' : 'small'));

const hasTourType = computed(
  () =>
    Boolean(props.tourTypes?.allowsInPersonTours) ||
    Boolean(props.tourTypes?.allowsVirtualTours) ||
    Boolean(props.tourTypes?.allowsSelfGuidedTours),
);
</script>
